import React, { useState } from 'react';
import { toast } from 'react-toastify';

import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import SendIcon from '@material-ui/icons/Send';

import { Table } from 'components';
import { useBoolean } from 'hooks/useBoolean';
import { useQuery } from 'hooks/useQuery';
import { PageTitle } from 'pages/Layout/PageTitle';
import { emailSendingService } from 'services';

import { EditEmailSendingConfigDialog } from './EditEmailSendingConfigDialog';
import { ViewLogsDialog } from './ViewLogsDialog';

const weekdayLabels = {
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
};

const columns = [
  { label: 'Cliente' },
  { label: 'Título' },
  { label: 'Destinatário' },
  { label: 'Dias' },
  { label: 'Anexo' },
  { label: 'Ações', align: 'center' },
];

const formatAttachment = (config) => {
  if (config.attachment_type !== 'signage_report') {
    return config.attachment_original_name || 'Manual (sem arquivo)';
  }

  const linkedClients =
    config.signage_report_clients === 'TODOS'
      ? 'TODOS'
      : config.signage_report_clients?.split(';').join(', ');

  return `Relatório Sinalização — ${linkedClients}`;
};

const ListEmailSendingConfigs = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sendingId, setSendingId] = useState(null);

  const [configs, , loadingConfigs, refetchConfigs] = useQuery(
    () => emailSendingService.listConfigs(),
    [],
  );

  const [isEditOpen, setIsEditState] = useBoolean();
  const [isLogsOpen, setIsLogsState] = useBoolean();
  const [selectedConfig, setSelectedConfig] = useState({});

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (config) => {
    setSelectedConfig(config);
    setIsEditState.toTrue();
  };

  const handleCloseEdit = () => {
    setIsEditState.toFalse();
  };

  const handleOpenLogs = (config) => {
    setSelectedConfig(config);
    setIsLogsState.toTrue();
  };

  const handleCloseLogs = () => {
    setIsLogsState.toFalse();
  };

  const handleDelete = async (config) => {
    if (!window.confirm(`Excluir a configuração "${config.title}"?`)) return;

    try {
      await emailSendingService.deleteConfig(config.id);
      toast.success('Configuração removida com sucesso.');
      refetchConfigs();
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Erro ao remover configuração.');
    }
  };

  const handleSendNow = async (config) => {
    if (!window.confirm(`Enviar o email "${config.title}" agora?`)) return;

    try {
      setSendingId(config.id);
      const { data } = await emailSendingService.sendNow(config.id);

      if (data?.status === 'no_data') {
        toast.info(data.message || 'Nenhum dado encontrado para o relatório — email não enviado.');
      } else {
        toast.success(data?.message || 'Email enviado com sucesso!');
      }
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Erro ao enviar email.');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <>
      <PageTitle>Configurações de envio de email</PageTitle>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Table
            headers={columns}
            striped
            emptyMessage='Nenhuma configuração cadastrada.'
            data={configs}
            loading={loadingConfigs}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            disableNumeration
          >
            {(configs || []).map((config) => (
              <TableRow key={config.id}>
                <TableCell>{config.client}</TableCell>
                <TableCell>{config.title}</TableCell>
                <TableCell>{config.recipients}</TableCell>
                <TableCell>{weekdayLabels[config.weekday]}</TableCell>
                <TableCell>{formatAttachment(config)}</TableCell>
                <TableCell align='center'>
                  <IconButton color='primary' onClick={() => handleOpenEdit(config)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color='primary'
                    disabled={sendingId === config.id}
                    onClick={() => handleSendNow(config)}
                  >
                    <SendIcon />
                  </IconButton>
                  <IconButton color='primary' onClick={() => handleOpenLogs(config)}>
                    <HistoryIcon />
                  </IconButton>
                  <IconButton color='secondary' onClick={() => handleDelete(config)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Grid>
      </Grid>
      <EditEmailSendingConfigDialog
        open={isEditOpen}
        handleClose={handleCloseEdit}
        config={selectedConfig}
        reloadConfigs={refetchConfigs}
      />
      <ViewLogsDialog open={isLogsOpen} handleClose={handleCloseLogs} config={selectedConfig} />
    </>
  );
};

export default ListEmailSendingConfigs;
